import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { GearSix, ArrowClockwise } from "@phosphor-icons/react";

const ROLES = ["admin", "directivo", "profesor", "jugador"];

// Master list of menu keys (kept in sync with Layout.jsx navKeys).
// Only the keys are needed; labels come from translations.
const NAV_KEYS = [
  "inicio", "clubes", "torneos", "retas", "clinicas", "marcador", "jugadores", "comunidad",
  "comic", "uso_plataforma", "chat", "tienda", "canchas", "estadisticas", "streaming",
  "pagos", "invitaciones", "config_pagos", "ingresos", "usuarios", "mapa_jugadores",
  "limpiar_historial", "trivia", "atari", "padel_game", "admin_flags", "admin_gear",
  "balonpie", "admin_visibility", "admin_access", "publicidad", "dyd_run", "reportes",
  "recuperacion", "dyd_coach", "dyd_coin", "plataforma", "dev", "dyd_access", "planes",
  "pagos_manuales", "wearables", "ligas", "mi_perfil", "agenda", "mi_tarjeta",
];

export default function MenuLayoutAdmin() {
  const { user } = useAuth();
  const [role, setRole] = useState("jugador");
  const [items, setItems] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = (r) => {
    api.get(`/menu-layout/${r}`).then(({ data }) => {
      // Ensure all NAV_KEYS present in the editable list, preserving stored order first
      const ordered = [...(data?.items || [])].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
      const seen = new Set(ordered.map((it) => it.key));
      for (const key of NAV_KEYS) {
        if (!seen.has(key)) ordered.push({ key, visible: true, label_override: null, order: ordered.length });
      }
      const filtered = ordered.filter((it) => NAV_KEYS.includes(it.key));
      setItems(filtered.map((it, i) => ({ ...it, order: i })));
    });
  };
  useEffect(() => { load(role); }, [role]);

  if (user && user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const move = (idx, delta) => {
    const t = idx + delta;
    if (t < 0 || t >= items.length) return;
    const arr = [...items];
    [arr[idx], arr[t]] = [arr[t], arr[idx]];
    setItems(arr.map((it, i) => ({ ...it, order: i })));
  };
  const toggleVisible = (idx) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], visible: !arr[idx].visible };
    setItems(arr);
  };
  const setLabel = (idx, val) => {
    const arr = [...items];
    arr[idx] = { ...arr[idx], label_override: val || null };
    setItems(arr);
  };

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const arr = [...items];
    const [m] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, m);
    setItems(arr.map((it, i) => ({ ...it, order: i })));
    setDragIdx(null);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/menu-layout/${role}`, { items });
      toast.success(`Menú del rol ${role} guardado`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`¿Restablecer el menú del rol ${role} al default?`)) return;
    try {
      await api.post(`/menu-layout/${role}/reset`);
      toast.success("Restablecido");
      load(role);
    } catch { toast.error("Error"); }
  };

  const showAll = () => setItems(items.map((it) => ({ ...it, visible: true })));
  const hideAll = () => setItems(items.map((it) => ({ ...it, visible: false })));

  return (
    <div className="space-y-4" data-testid="menu-layout-admin-page">
      <header className="flex items-center gap-3">
        <GearSix size={30} weight="fill" className="text-[#CCFF00]" />
        <div>
          <h1 className="text-2xl md:text-3xl font-['Barlow_Condensed'] font-black uppercase tracking-wider text-white">Ajustar menú lateral</h1>
          <p className="text-sm text-gray-400">Activa/desactiva y reordena los items del sidebar por rol.</p>
        </div>
      </header>

      <Tabs value={role} onValueChange={setRole}>
        <TabsList className="bg-white/5 border border-white/10">
          {ROLES.map((r) => <TabsTrigger key={r} value={r} className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black text-xs uppercase" data-testid={`menu-tab-${r}`}>{r}</TabsTrigger>)}
        </TabsList>
        {ROLES.map((r) => (
          <TabsContent key={r} value={r} className="mt-4">
            <div className="flex items-center gap-2 mb-3">
              <Button onClick={save} disabled={saving} className="bg-[#CCFF00] text-black font-bold" data-testid="menu-save-btn">
                {saving ? "Guardando…" : `Guardar menú ${r}`}
              </Button>
              <Button onClick={reset} variant="outline" className="border-white/20 text-white text-xs" data-testid="menu-reset-btn">
                <ArrowClockwise size={14} className="mr-1" /> Reset
              </Button>
              <Button onClick={showAll} variant="outline" className="border-white/20 text-white text-xs" data-testid="menu-show-all">Mostrar todos</Button>
              <Button onClick={hideAll} variant="outline" className="border-white/20 text-white text-xs" data-testid="menu-hide-all">Ocultar todos</Button>
              <Badge className="bg-black/60 text-[10px] uppercase border-white/10">{items.filter((it) => it.visible !== false).length} / {items.length} visibles</Badge>
            </div>
            <ul className="space-y-1.5">
              {items.map((it, i) => (
                <li key={it.key}
                    draggable
                    onDragStart={() => onDragStart(i)}
                    onDragOver={onDragOver}
                    onDrop={() => onDrop(i)}
                    className={`flex items-center gap-2 p-2 border rounded cursor-move text-white ${dragIdx === i ? "bg-[#CCFF00]/10 border-[#CCFF00]" : "bg-white/5 border-white/10 hover:border-white/20"}`}
                    data-testid={`menu-item-${it.key}`}>
                  <span className="text-[10px] font-mono text-gray-500 w-8 text-right">{i + 1}</span>
                  <code className="text-[11px] text-[#CCFF00]/80 w-32 truncate uppercase tracking-wider">{it.key}</code>
                  <Input
                    placeholder="Etiqueta personalizada (opcional)"
                    value={it.label_override || ""}
                    onChange={(e) => setLabel(i, e.target.value)}
                    className="bg-black/50 border-white/10 text-white flex-1 h-8 text-xs"
                    data-testid={`menu-label-${it.key}`}
                  />
                  <Switch checked={it.visible !== false} onCheckedChange={() => toggleVisible(i)} data-testid={`menu-visible-${it.key}`} />
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => move(i, -1)} data-testid={`menu-up-${it.key}`}>↑</Button>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-gray-400" onClick={() => move(i, +1)} data-testid={`menu-down-${it.key}`}>↓</Button>
                </li>
              ))}
            </ul>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
